import { HttpClient, HttpErrorResponse, HttpEvent, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, Subject, catchError, tap, throwError } from "rxjs";
import { environment } from "src/environments/environment";
import { PageableResponse } from "./pageable-response";
import { VinLookupVehicle } from "./vin-lookup-vehicle";
import { VehicleDropdownData } from "./vehicle-dropdown-data";
import { InventoryUnit } from "./inventory-unit";
import { InventoryUnitDetails } from "./inventory-unit-details";
import { FormGroup } from "@angular/forms";
import { CurrencyPipe } from "@angular/common";
import { Vendor } from "./upload/models/vendor";
import { SelectOption } from "../shared/models/select-option";
import { InventoryUnitCost } from "./vehicle-costs/models/inventory-unit-cost";
import { InventoryUnitCostWrapper } from "./vehicle-costs/models/inventory-unit-cost-wrapper";
import { Vehicle } from "./vehicle";
import { ErrorHandler } from "../shared/errors/error-handling";
import { Make } from "./make";
import { Trim } from "./trim";
import { CreateTrimRequest } from "./trim-request";
import { Engine } from "./engine";
import { InventoryUnitFormData } from "./upload/models/inventory-unit-form-data";
import { InventoryUnitRequest } from "./inventory-unit-request";
import { Tag } from "./publish/models/tag.model";
import { PublishInfoUpdate } from "./publish/models/publish-info-update.model";

@Injectable({
    providedIn: 'root'
})
export class VehicleService {

    // private vehicleUrl = 'api/vehicles/vehicles.json';
    private inventoryUrl = environment.apiUrl + '/v1/inventory';
    // private vinLookupUrl = environment.apiUrl + '/v1/catalog/vin';
    private vinLookupUrl = environment.apiUrl + '/v2/vehicles/vin';
    private vendorUrl = environment.apiUrl + '/v1/vendors';
    private costUrl = environment.apiUrl + '/v1/costs';
    private attributesUrl = environment.apiUrl + '/v1/attributes';
    private inventoryUnitId?: string;
    private inventoryUnitDetailsResponse?: InventoryUnitDetails;
    private vinLookupVehicleResponse?: VinLookupVehicle;

    private vinLookupVehicle = new Subject<VinLookupVehicle>();
    private inventoryUnit = new Subject<InventoryUnit>();
    private inventoryUnitDetails = new Subject<InventoryUnitDetails>();

    vinLookupVehicleChanged$ = this.vinLookupVehicle.asObservable();
    inventoryUnitChanged$ = this.inventoryUnit.asObservable();
    inventoryUnitDetailsChanged$ = this.inventoryUnitDetails.asObservable();

    constructor(private http: HttpClient, private currencyPipe: CurrencyPipe) { }

    getVehicles(status: string = 'available', pageNumber: number = 0, pageSize: number = 5): Observable<PageableResponse<Vehicle>> {
        return this.http.get<PageableResponse<Vehicle>>(`${this.inventoryUrl}?status=${status}&page=${pageNumber}&size=${pageSize}`).pipe(
            tap(data => console.log('All', JSON.stringify(data))),
            catchError(ErrorHandler.handleError)
        );
    }

    getVehicleByVin(vin: string): Observable<VinLookupVehicle> {
        const url = `${this.vinLookupUrl}/${vin}`;
        
        return this.http.get<VinLookupVehicle>(`${this.vinLookupUrl}/${vin}`).pipe(
            tap(data => {
                console.log('Vin Lookup Response', JSON.stringify);
                this.vinLookupVehicle.next(data);
            }),
            catchError(ErrorHandler.handleError)
        );
    }

    getInventoryUnitDetailsByVin(vin: string): Observable<InventoryUnitDetails> {
        const url = `${this.inventoryUrl}/vehicles/${vin}`;
        
        return this.http.get<InventoryUnitDetails>(url).pipe(
            tap(data => {
                console.log('Inventory Unit Details Response', JSON.stringify);
                this.inventoryUnitDetails.next(data);
            }),
            // catchError(ErrorHandler.handleError)
        );
    }

    getInventoryUnitDetailsById(id: string): Observable<InventoryUnitDetails> {
        const url = `${this.inventoryUrl}/${id}`;
        
        return this.http.get<InventoryUnitDetails>(url).pipe(
            tap(data => {
                console.log('Inventory Unit Details Response', JSON.stringify);
                this.inventoryUnitDetails.next(data);
            }),
            catchError(ErrorHandler.handleError)
        );
    }

    getTags(): Observable<Tag[]> {
        const url = `${this.attributesUrl}/tags`;
        return this.http.get<Tag[]>(url).pipe(
            catchError(ErrorHandler.handleError)
        );
    }

    updatePublishInfo(inventoryUnitId: string, payload: PublishInfoUpdate): Observable<InventoryUnitDetails> {
        const url = `${this.inventoryUrl}/${inventoryUnitId}/listing`;
        return this.http.put<InventoryUnitDetails>(url, payload).pipe(
            tap(data => this.inventoryUnitDetails.next(data)),
            catchError(ErrorHandler.handleError)
        );
    }

    getMakesWithModels(): Observable<Make[]> {
        const url = this.attributesUrl + '/makes?includeModels=true';

        return this.http.get<Make[]>(url).pipe(
            tap(data => {
                console.log('makes with models', data);
            })
        )
    }

    getInventoryUnitFormData(): Observable<InventoryUnitFormData> {
        const url = this.attributesUrl + '/inventory-form';
        return this.http.get<InventoryUnitFormData>(url).pipe();
    }

    getVehicleEditFormData(): Observable<VehicleDropdownData> {
        const url = `${this.attributesUrl}`;
        
        return this.http.get<VehicleDropdownData>(`${this.attributesUrl}`).pipe(
            tap(data => console.log('Vehicle Dropdown data', JSON.stringify)),
            catchError(ErrorHandler.handleError)
        );
    }


    getTrimsByYearMakeIdModelId(year: string, makeId: string, modelId: string): Observable<Trim[]> {
        const url = `${this.attributesUrl}/trims?year=${year}&makeId=${makeId}&modelId=${modelId}`;
        console.log('Trims URL', url);
        
        return this.http.get<Trim[]>(url).pipe(
            tap(data => console.log('Trim with Styles data', JSON.stringify)),
            catchError(ErrorHandler.handleError)
        );
    }

    getVehicleAttributes(): Observable<any> {
        const url = `${this.attributesUrl}?include=bodyStyles,fuelTypes,drivetrains,transmissions`;
        console.log('Trim Attributes URL', url);
        
        return this.http.get<any>(url).pipe(
            tap(data => console.log('Trim Attributes data', data)),
            catchError(ErrorHandler.handleError)
        );
    }

    getEnginesByYearMakeModel(year: string, makeId: string, modelId: string): Observable<Engine[]> {
        const url = `${this.attributesUrl}/engines`;
        const requestBody = {
            year: Number(year),
            makeId: Number(makeId),
            modelId: Number(modelId)
        };
        console.log('Fetching engines with:', requestBody);
        
        return this.http.post<Engine[]>(url, requestBody).pipe(
            tap(data => console.log('Engines data:', data)),
            catchError(ErrorHandler.handleError)
        );
    }

    createTrim(trimData: CreateTrimRequest): Observable<Trim> {
        const url = `${this.attributesUrl}/trims`;
        console.log('Creating trim with data:', trimData);
        
        return this.http.post<Trim>(url, trimData).pipe(
            tap(data => console.log('Trim created:', data)),
            catchError(ErrorHandler.handleError)
        );
    }

    getGenericColors(): Observable<any> {
        const url = `${this.attributesUrl}/colors?isGeneric=true`;
        console.log('Generic Colors URL', url);
        
        return this.http.get<any>(url).pipe(
            tap(data => console.log('Generic Colors data', data)),
            catchError(ErrorHandler.handleError)
        );
    }

    createInventoryUnit(requestBody: InventoryUnitRequest): Observable<InventoryUnit> {

        console.log('making api request to CREATE inventory unit', requestBody);

        return this.http.post<InventoryUnit>(this.inventoryUrl, requestBody).pipe(
            tap(data => {
                console.log('inventory unit saved', data);
                this.inventoryUnit.next(data);
            }),
            catchError(ErrorHandler.handleError),
        );
    }

    updateInventoryUnit(requestBody: InventoryUnitRequest, inventoryUnitId: string): Observable<InventoryUnit> {

        console.log('making api request to UPDATE inventory unit', requestBody);
        console.log('inventoryUnitId to be sent in update request', inventoryUnitId);

        let url = this.inventoryUrl + '/' + inventoryUnitId;

        return this.http.put<InventoryUnit>(url, requestBody).pipe(
            tap(data => {
                console.log('inventory unit saved', data);
                this.inventoryUnit.next(data);
            }),
            catchError(ErrorHandler.handleError),
        );
    }

    updateInventoryUnitStatus(inventoryUnitId: string, status: string): Observable<HttpResponse<any>> {
        const url = `${this.inventoryUrl}/${inventoryUnitId}/status`;
        
        return this.http.patch(url, {status: status},  { observe: 'response', responseType: 'json' })
          .pipe(catchError(ErrorHandler.handleError));    
    }

    createInventoryUnitCost(inventoryUnitId: string, inventoryUnitCostForm: FormGroup): Observable<HttpResponse<InventoryUnitCost>> {
        console.log('making api request to CREATE inventory unit cost', inventoryUnitCostForm.value);

        return this.http.post<HttpResponse<InventoryUnitCost>>(`${this.costUrl}/inventory/${inventoryUnitId}`, inventoryUnitCostForm.value);
    }

    updateInventoryUnitCost(costId: number, inventoryUnitCostForm: FormGroup): Observable<HttpResponse<any>> {
        const url = `${this.costUrl}/${costId}`;
        
        return this.http.put(url, inventoryUnitCostForm.value,  { observe: 'response', responseType: 'json' });
    }

    deleteInventoryUnitCost(costId: number): Observable<HttpResponse<any>> {
        const url = `${this.costUrl}/${costId}`;
        
        return this.http.delete(url, { observe: 'response', responseType: 'json' })
          .pipe(catchError(ErrorHandler.handleError));    
    }

    getInventoryUnitCosts(inventoryUnitId: string): Observable<InventoryUnitCostWrapper> {
        return this.http.get<InventoryUnitCostWrapper>(`${this.costUrl}/inventory/${inventoryUnitId}`).pipe(
            // tap(data => console.log('All', JSON.stringify(data))),
            catchError(ErrorHandler.handleError)
        );
    }

    getVendors(): Observable<Vendor[]> {
        const url = `${this.vendorUrl}`;
        
        return this.http.get<Vendor[]>(url).pipe(
            tap(data => JSON.stringify),
            catchError(ErrorHandler.handleError)
        );
    }

    createVendor(vendorName: string): Observable<HttpResponse<Vendor>> {
        return this.http.post<HttpResponse<Vendor>>(`${this.vendorUrl}`, {name: vendorName}).pipe(
            // catchError(this.handleError),
        );
    }

    getCostTypes(): Observable<SelectOption[]> {
        const url = `${this.costUrl}`;
        
        return this.http.get<SelectOption[]>(`${url}/cost-types`).pipe(
            tap(data => JSON.stringify),
            catchError(ErrorHandler.handleError)
        );
    }

    getPaymentTypes(): Observable<SelectOption[]> {
        const url = `${this.costUrl}`;
        
        return this.http.get<SelectOption[]>(`${url}/payment-types`).pipe(
            tap(data => JSON.stringify),
            catchError(ErrorHandler.handleError)
        );
    }

    getApTypes(): Observable<SelectOption[]> {
        const url = `${this.costUrl}`;
        
        return this.http.get<SelectOption[]>(`${url}/ap-types`).pipe(
            tap(data => JSON.stringify),
            catchError(ErrorHandler.handleError)
        );
    }

    setInventoryUnitIdFromRoute(id: string): void {
        this.inventoryUnitId = id;
    }

    getInventoryUnitIdFromRoute(): string | undefined {
        return this.inventoryUnitId;
    }

    setInventoryUnitDetails(inventoryUnitDetails: InventoryUnitDetails) {
        this.inventoryUnitDetailsResponse = inventoryUnitDetails;
    }

    getInventoryUnitDetails(): InventoryUnitDetails | undefined {
        return this.inventoryUnitDetailsResponse;
    }

    setVinLookupVehicle(vinLookupVehicle: VinLookupVehicle) {
        this.vinLookupVehicleResponse = vinLookupVehicle;
    }

    getVinLookupVehicle(): VinLookupVehicle | undefined {
        return this.vinLookupVehicleResponse;
    }

    buildPageHeader(header: {
        year?: string,
        make?: string,
        model?: string,
        trim?: string,
        vin?: string,
        mileage?: string,
        price?: string,
        imageUrl: string,
        isCreateMode: boolean
      }) {
        console.log('header data', header);
        const yearMakeModelTrim = [header.year, header.make, header.model, header.trim].filter(Boolean).join(' ');

        let mileage = '';
        if(header.mileage) {
            const formattedMileage = Number(header.mileage).toLocaleString();
            mileage = `${formattedMileage} Miles`;
        } else {
            mileage = 'No mileage entered';
        }

        let price = '';
        if(header.price) {
            const formattedPrice = this.currencyPipe.transform(header.price, 'USD', '$', '1.2-2');
            price = `Price: ${formattedPrice}`;
        }

        const pageHeading = [yearMakeModelTrim, mileage, price].filter(Boolean).join('<br>');

        const pageSubHeading = `VIN# ${header.vin}`;
        return {
            heading: pageHeading,
            subHeading: pageSubHeading,
            imageUrl: header.imageUrl
        }
    }
}